import type {
  AttendanceDashboardElasticSource,
  AttendanceDashboardItem,
  AttendanceDashboardListResult,
  AttendanceDashboardRecordSummary,
  AttendanceLeaveType,
  TimestampSeconds,
} from '@src/models/interfaces/attendanceDashboard.interface';
import { ATTENDANCE_LEAVE_TYPE } from '@src/models/interfaces/attendanceLeaveType';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type {
  ElasticSearchHit,
  ElasticSearchResponse,
} from '@src/models/interfaces/general/ElasticSearchResponseInterface';
import { ElasticResponseMapper } from '@src/models/mappers/commons/fromElasticResponse';
import { DateUtil } from '@src/packages/utils/dates';
import { addressFormat, type AddressFormatInput } from '@src/util/format/addressFormat';
import { formatUserFullName } from '@src/util/format/userFullName';

export class AttendanceDashboardMapper {
  private static normalizeLeaveType(value: unknown): AttendanceLeaveType | null {
    if (
      value === ATTENDANCE_LEAVE_TYPE.JUSTIFIED ||
      value === ATTENDANCE_LEAVE_TYPE.UNJUSTIFIED
    ) {
      return value;
    }
    if (
      value === ATTENDANCE_LEAVE_TYPE.NONE ||
      value === null ||
      value === undefined
    ) {
      return null;
    }
    return null;
  }

  private static normalizeObservations(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed || null;
  }

  private static readTimestampSeconds(value: unknown): number | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const record = value as Record<string, unknown>;
    if (typeof record._seconds === 'number') {
      return record._seconds;
    }
    if (typeof record.seconds === 'number') {
      return record.seconds;
    }

    return null;
  }

  private static readTimestampNanoseconds(value: unknown): number {
    if (!value || typeof value !== 'object') {
      return 0;
    }

    const record = value as Record<string, unknown>;
    if (typeof record._nanoseconds === 'number') {
      return record._nanoseconds;
    }
    if (typeof record.nanoseconds === 'number') {
      return record.nanoseconds;
    }

    return 0;
  }

  /**
   * Firestore `onSnapshot` delivers native Timestamps (`seconds` / `toMillis`).
   * Elastic hits use `{ _seconds }`. Status and KPIs only read `_seconds`.
   */
  private static normalizeTimestamp(value: unknown): TimestampSeconds | null {
    const seconds = AttendanceDashboardMapper.readTimestampSeconds(value);
    if (seconds !== null) {
      return {
        _seconds: seconds,
        _nanoseconds: AttendanceDashboardMapper.readTimestampNanoseconds(value),
      };
    }

    const ms = DateUtil.getTimestampMs(value);
    if (ms === null) {
      return null;
    }

    const wholeSeconds = Math.floor(ms / 1000);
    return {
      _seconds: wholeSeconds,
      _nanoseconds: Math.round((ms - wholeSeconds * 1000) * 1e6),
    };
  }

  private static toLocationLabel(address: unknown): string | null {
    if (!address || typeof address !== 'object') {
      return null;
    }
    // Elastic/Firestore address is partial; AddressFormatInput matches what addressFormat reads.
    const label = addressFormat(address as AddressFormatInput).trim();
    return label || null;
  }

  private static normalizeCostCenterId(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }
    const trimmed = value.trim();
    return trimmed || null;
  }

  static mapFirestoreAttendanceRecordOverlay(
    docData: Record<string, unknown>,
    currentItem: AttendanceDashboardItem,
  ): AttendanceDashboardItem {
    const workShiftId =
      typeof docData.workShiftId === 'string' ? docData.workShiftId : currentItem.workShiftId;

    const toleranceFromRecord =
      typeof docData.toleranceTime === 'number' ? docData.toleranceTime : undefined;

    return {
      ...currentItem,
      workShiftId,
      toleranceTimeMinutes: toleranceFromRecord ?? currentItem.toleranceTimeMinutes,
      actualStartAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.actualStartAt) ??
        currentItem.actualStartAt,
      actualEndAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.actualEndAt) ??
        currentItem.actualEndAt,
      actualBreakStartAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.actualBreakStartAt) ??
        currentItem.actualBreakStartAt,
      actualBreakEndAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.actualBreakEndAt) ??
        currentItem.actualBreakEndAt,
      scheduledStartAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.scheduledStartAt) ??
        currentItem.scheduledStartAt,
      scheduledEndAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.scheduledEndAt) ??
        currentItem.scheduledEndAt,
      scheduledBreakStartAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.scheduledBreakStartAt) ??
        currentItem.scheduledBreakStartAt,
      scheduledBreakEndAt:
        AttendanceDashboardMapper.normalizeTimestamp(docData.scheduledBreakEndAt) ??
        currentItem.scheduledBreakEndAt,
      locationLabel:
        AttendanceDashboardMapper.toLocationLabel(docData.address) ?? currentItem.locationLabel,
      attendanceComplete:
        typeof docData.attendanceComplete === 'boolean'
          ? docData.attendanceComplete
          : currentItem.attendanceComplete,
      // Explicit null/none in Firestore must clear the Elastic leave; only fall back when the key is absent.
      leaveType:
        Object.prototype.hasOwnProperty.call(docData, 'leaveType')
          ? AttendanceDashboardMapper.normalizeLeaveType(docData.leaveType)
          : currentItem.leaveType,
      observations: Object.prototype.hasOwnProperty.call(docData, 'observations')
        ? AttendanceDashboardMapper.normalizeObservations(docData.observations)
        : currentItem.observations,
      costCenterId: Object.prototype.hasOwnProperty.call(docData, 'costCenterId')
        ? AttendanceDashboardMapper.normalizeCostCenterId(docData.costCenterId)
        : currentItem.costCenterId,
    };
  }

  private static fromElasticSource(
    source: AttendanceDashboardElasticSource,
    hitId: string,
  ): AttendanceDashboardItem {
    const record: AttendanceDashboardRecordSummary = source.attendanceRecord ?? {
      id: hitId,
      employeeId: '',
    };
    const config = source.workJourneyConfig;
    const user = source.user;

    const journeyName = config?.name?.trim() || '—';
    const userFirstName = user?.firstName?.trim() || null;
    const userLastName = user?.lastName?.trim() || null;
    const userDisplayName = formatUserFullName({ firstName: userFirstName, lastName: userLastName }) || '—';
    const userIdCard = user?.idCard?.trim() || null;
    const userEmail = user?.email?.trim() || null;

    const toleranceFromRecord =
      typeof record.toleranceTime === 'number' ? record.toleranceTime : undefined;
    const toleranceFromConfig =
      typeof config?.toleranceTime === 'number' ? config.toleranceTime : undefined;

    return {
      id: record.id || source.id || hitId,
      organizationId: source.organizationId || record.organizationId || '',
      employeeId: record.employeeId || user?.id || '',
      workJourneyConfigId: record.workJourneyConfigId ?? config?.id ?? null,
      workShiftId: record.workShiftId ?? null,
      regulationName: null,
      scheduledStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledStartAt),
      scheduledEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledEndAt),
      scheduledBreakStartAt: AttendanceDashboardMapper.normalizeTimestamp(
        record.scheduledBreakStartAt,
      ),
      scheduledBreakEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledBreakEndAt),
      actualStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualStartAt),
      actualEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualEndAt),
      actualBreakStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualBreakStartAt),
      actualBreakEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualBreakEndAt),
      attendanceComplete: Boolean(record.attendanceComplete),
      leaveType: AttendanceDashboardMapper.normalizeLeaveType(record.leaveType),
      observations: AttendanceDashboardMapper.normalizeObservations(record.observations),
      toleranceTimeMinutes: toleranceFromRecord ?? toleranceFromConfig ?? 0,
      requiredMembers: config?.requiredMembers ?? 0,
      journeyName,
      userDisplayName,
      userFirstName,
      userLastName,
      userIdCard,
      userEmail,
      workJourneyConfigState: config?.state ?? null,
      locationLabel: AttendanceDashboardMapper.toLocationLabel(record.address),
      costCenterId: AttendanceDashboardMapper.normalizeCostCenterId(
        record.costCenterId ?? (source as { costCenterId?: unknown }).costCenterId,
      ),
    };
  }

  static fromAttendanceRecord(record: IAttendanceRecord): AttendanceDashboardItem {
    return {
      id: record.id,
      organizationId: record.organizationId ?? '',
      employeeId: record.employeeId ?? '',
      workJourneyConfigId: record.workJourneyConfigId ?? null,
      workShiftId: record.workShiftId ?? null,
      regulationName: null,
      scheduledStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledStartAt),
      scheduledEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledEndAt),
      scheduledBreakStartAt: AttendanceDashboardMapper.normalizeTimestamp(
        record.scheduledBreakStartAt,
      ),
      scheduledBreakEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.scheduledBreakEndAt),
      actualStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualStartAt),
      actualEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualEndAt),
      actualBreakStartAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualBreakStartAt),
      actualBreakEndAt: AttendanceDashboardMapper.normalizeTimestamp(record.actualBreakEndAt),
      attendanceComplete: Boolean(record.attendanceComplete),
      leaveType: AttendanceDashboardMapper.normalizeLeaveType(record.leaveType),
      observations: AttendanceDashboardMapper.normalizeObservations(record.observations),
      toleranceTimeMinutes: typeof record.toleranceTime === 'number' ? record.toleranceTime : 0,
      requiredMembers: 0,
      journeyName: '',
      userDisplayName: record.employeeFullname?.trim() || '',
      userFirstName: null,
      userLastName: null,
      userIdCard: record.employeeIdentifier?.trim() || null,
      userEmail: null,
      workJourneyConfigState: null,
      locationLabel: AttendanceDashboardMapper.toLocationLabel(record.address),
      costCenterId: AttendanceDashboardMapper.normalizeCostCenterId(record.costCenterId),
    };
  }

  static fromElasticHit(
    hit: ElasticSearchHit<AttendanceDashboardElasticSource>,
  ): AttendanceDashboardItem {
    return ElasticResponseMapper.fromHit(hit, AttendanceDashboardMapper.fromElasticSource);
  }

  static fromElasticResponse(
    response: ElasticSearchResponse<AttendanceDashboardElasticSource> | null,
  ): AttendanceDashboardListResult {
    return ElasticResponseMapper.fromResponse(
      response,
      AttendanceDashboardMapper.fromElasticSource,
    );
  }
}
